import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import './NoteApp.css';
import { useDispatch, useSelector } from 'react-redux';
import { getNotes, createNote, updateNote, deleteNote } from 'apiServices/reducer/apiService';
import { DeleteNotes } from 'pageComponents';
import CustomSuccessModal from '../../../components/CustomSuccessModal';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import FontFamily from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import { Node } from '@tiptap/core';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Underline as LucideUnderline,
  Strikethrough as LucideStrikethrough,
  Subscript as LucideSubscript,
  Superscript as LucideSuperscript,
  Code as LucideCode,
  Image as LucideImage,
  Video as LucideVideo,
  Link as LucideLink,
  Highlighter
} from 'lucide-react';

// Constants for dropdowns and color palette
const FONT_SIZES = [
  { label: 'Small', value: '12px' },
  { label: 'Normal', value: '16px' },
  { label: 'Large', value: '20px' },
  { label: 'Huge', value: '32px' },
];
const HEADINGS = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 1 },
  { label: 'Heading 2', value: 2 },
  { label: 'Heading 3', value: 3 },
];
const FONT_FAMILIES = [
  { label: 'Sans Serif', value: 'Arial' },
  { label: 'Serif', value: 'Times New Roman' },
  { label: 'Monospace', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: 'Verdana', value: 'Verdana' },
];

const COLOR_PALETTE = [
  '#000000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#0000FF', '#9900FF', '#FF00FF',
  '#808080', '#FF3333', '#FFCC00', '#FFFF99', '#99FF99', '#99FFFF', '#3399FF', '#9933FF', '#FF33CC',
  '#C0C0C0', '#FF6666', '#FFCC66', '#FFFFCC', '#CCFFCC', '#CCFFFF', '#66CCFF', '#CC99FF', '#FF99CC',
  '#FFFFFF', '#FF9999', '#FFCC99', '#FFFFE0', '#E0FFE0', '#E0FFFF', '#99CCFF', '#E0CCFF', '#FFCCE0',
  '#666666', '#B20000', '#B26B00', '#B2B200', '#006600', '#006666', '#000099', '#6B006B', '#B2006B',
];

// Custom Video Modal Component
const VideoModal = ({ show, onHide, onInsert }) => {
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    if (show) {
      setVideoUrl('');
    }
  }, [show]);

  const convertToEmbedUrl = (url) => {
    if (!url) return '';

    // If it's already an iframe, return as is
    if (url.includes('<iframe')) return url;

    // Convert YouTube watch URL to embed URL
    const youtubeWatchRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
    const youtubeMatch = url.match(youtubeWatchRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Convert Vimeo URL to embed URL
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // For other URLs, assume they're already embed-ready
    return url;
  };

  const handleSave = () => {
    if (!videoUrl.trim()) {
      alert('Please enter a video URL');
      return;
    }

    const embedUrl = convertToEmbedUrl(videoUrl);

    // Create iframe node data for the editor
    const iframeData = {
      src: embedUrl,
      title: 'Video',
      frameborder: '0',
      allowfullscreen: true,
      allow: 'autoplay; encrypted-media; picture-in-picture',
      style: 'width: 100%; height: 315px; max-width: 560px; margin: 10px 0; border-radius: 4px;'
    };

    onInsert(iframeData);
    onHide();
  };

  if (!show) return null;

  return (
    <div className="video-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    }}>
      <div className="video-modal-content" style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        minWidth: '400px',
        maxWidth: '500px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Insert Video</h3>
        <p style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
          Enter a YouTube or Vimeo URL to embed a video:
        </p>
        <input
          type="text"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '15px'
          }}
        />
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onHide}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#007bff',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom Toolbar Component - moved outside NoteApp to prevent state resets
const Toolbar = ({
  editor,
  composeMode,
  editMode,
  isImageUploading,
  imageInputRef,
  handleImageUpload,
  showVideoModal,
  setShowVideoModal,
  currentTextColor,
  setCurrentTextColor,
  currentHighlightColor,
  setCurrentHighlightColor,
  showTextColorPalette,
  setShowTextColorPalette,
  showHighlightPalette,
  setShowHighlightPalette,
  textColorBtnRef,
  highlightBtnRef
}) => {
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [currentFontFamily, setCurrentFontFamily] = useState('Arial');

  // Track editor attribute changes
  useEffect(() => {
    if (editor) {
      const fontSizeAttrs = editor.getAttributes('fontSize');
      const fontFamilyAttrs = editor.getAttributes('fontFamily');

      if (fontSizeAttrs?.fontSize) {
        setCurrentFontSize(fontSizeAttrs.fontSize);
      }

      if (fontFamilyAttrs?.fontFamily) {
        setCurrentFontFamily(fontFamilyAttrs.fontFamily);
      }
    }
  }, [editor]);

  // Custom dropdown states
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [showFontFamilyDropdown, setShowFontFamilyDropdown] = useState(false);

  // Custom dropdown refs
  const fontSizeDropdownRef = useRef(null);
  const fontFamilyDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fontSizeDropdownRef.current && !fontSizeDropdownRef.current.contains(event.target)) {
        setShowFontSizeDropdown(false);
      }
      if (fontFamilyDropdownRef.current && !fontFamilyDropdownRef.current.contains(event.target)) {
        setShowFontFamilyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation for dropdowns
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!showFontSizeDropdown && !showFontFamilyDropdown) return;

      const currentDropdown = showFontSizeDropdown ? 'fontSize' : 'fontFamily';
      const options = currentDropdown === 'fontSize' ? FONT_SIZES : FONT_FAMILIES;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          if (currentDropdown === 'fontSize') {
            setShowFontSizeDropdown(false);
          } else {
            setShowFontFamilyDropdown(false);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showFontSizeDropdown, showFontFamilyDropdown, editor]);

  const handleFontSizeSelect = (fontSize) => {
    console.log('Setting font size to:', fontSize);
    console.log('Available editor commands:', Object.keys(editor.commands));

    try {
      // Try multiple approaches to set font size
      let success = false;

      // Method 1: Use setFontSize command
      try {
        success = editor.chain().focus().setFontSize(fontSize).run();
        console.log('Method 1 (setFontSize) result:', success);
      } catch (error) {
        console.log('Method 1 failed:', error);
      }

      // Method 2: Use setMark if Method 1 failed
      if (!success) {
        try {
          success = editor.chain().focus().setMark('fontSize', { fontSize }).run();
          console.log('Method 2 (setMark) result:', success);
        } catch (error) {
          console.log('Method 2 failed:', error);
        }
      }

      // Method 3: Use setTextStyle if previous methods failed
      if (!success) {
        try {
          success = editor.chain().focus().setTextStyle({ fontSize }).run();
          console.log('Method 3 (setTextStyle) result:', success);
        } catch (error) {
          console.log('Method 3 failed:', error);
        }
      }

      console.log('Font size command executed successfully');

      if (success) {
        setCurrentFontSize(fontSize);
        setTimeout(() => {
          console.log('Updated font size attributes:', editor.getAttributes('fontSize'));
        }, 100);
      }
    } catch (error) {
      console.error('Error setting font size:', error);
    }
    setShowFontSizeDropdown(false);
  };

  const handleFontFamilySelect = (fontFamily) => {
    console.log('Setting font family to:', fontFamily);
    console.log('Available editor commands:', Object.keys(editor.commands));

    try {
      // Try multiple approaches to set font family
      let success = false;

      // Method 1: Use setFontFamily command
      try {
        success = editor.chain().focus().setFontFamily(fontFamily).run();
        console.log('Method 1 (setFontFamily) result:', success);
      } catch (error) {
        console.log('Method 1 failed:', error);
      }

      // Method 2: Use setMark if Method 1 failed
      if (!success) {
        try {
          success = editor.chain().focus().setMark('fontFamily', { fontFamily }).run();
          console.log('Method 2 (setMark) result:', success);
        } catch (error) {
          console.log('Method 2 failed:', error);
        }
      }

      // Method 3: Use setTextStyle if previous methods failed
      if (!success) {
        try {
          success = editor.chain().focus().setTextStyle({ fontFamily }).run();
          console.log('Method 3 (setTextStyle) result:', success);
        } catch (error) {
          console.log('Method 3 failed:', error);
        }
      }

      console.log('Font family command executed successfully');

      if (success) {
        setCurrentFontFamily(fontFamily);
        setTimeout(() => {
          console.log('Updated font family attributes:', editor.getAttributes('fontFamily'));
        }, 100);
      }
    } catch (error) {
      console.error('Error setting font family:', error);
    }
    setShowFontFamilyDropdown(false);
  };

  if (!editor) return null;

  // Get current font attributes
  const fontSizeAttrs = editor.getAttributes('fontSize');
  const fontFamilyAttrs = editor.getAttributes('fontFamily');

  console.log('FontSize attributes:', fontSizeAttrs);
  console.log('FontFamily attributes:', fontFamilyAttrs);

  // Check if there's a selection and get attributes from the selection
  const { from, to } = editor.state.selection;
  let fontSize = '16px';
  let fontFamily = 'Arial';

  if (from !== to) {
    // There's a selection, try to get attributes from the selection
    console.log('Selection found from', from, 'to', to);
    const marks = editor.state.doc.textBetween(from, to, undefined, undefined, (node, pos) => {
      return node.marks;
    });
    console.log('Selection marks:', marks);
  }

  // Check if the editor has any active marks
  const activeMarks = editor.state.selection.$from.marks();
  console.log('Active marks:', activeMarks);

  // Get the current font size and family from attributes or state
  fontSize = fontSizeAttrs?.fontSize || currentFontSize || '16px';
  fontFamily = fontFamilyAttrs?.fontFamily || currentFontFamily || 'Arial';

  console.log('Current font size:', fontSize);
  console.log('Current font family:', fontFamily);

  return (
    <div className="tiptap-toolbar exact-toolbar" style={{ overflow: 'visible' }}>
      {/* Bold, Italic, Underline, Strikethrough */}
      <button onClick={() => { editor.chain().focus().toggleBold().run(); }} className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`} title="Bold"><Bold size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleItalic().run(); }} className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`} title="Italic"><Italic size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleUnderline().run(); }} className={`toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`} title="Underline"><LucideUnderline size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleStrike().run(); }} className={`toolbar-button ${editor.isActive('strike') ? 'is-active' : ''}`} title="Strikethrough"><LucideStrikethrough size={16} /></button>
      <div className="toolbar-divider"></div>
      {/* Blockquote, Inline Code, Code Block */}
      <button onClick={() => { editor.chain().focus().toggleBlockquote().run(); }} className={`toolbar-button ${editor.isActive('blockquote') ? 'is-active' : ''}`} title="Quote"><Quote size={16} /></button>
      <button onClick={() => { console.log('Inline code button clicked'); editor.chain().focus().toggleCode().run(); }} className={`toolbar-button ${editor.isActive('code') ? 'is-active' : ''}`} title="Inline Code">{'<>'}</button>
      <button onClick={() => { editor.chain().focus().toggleCodeBlock().run(); }} className={`toolbar-button ${editor.isActive('codeBlock') ? 'is-active' : ''}`} title="Code Block">{'{ }'}</button>
      <div className="toolbar-divider"></div>
      {/* Headings and Paragraph */}
      <button onClick={() => { editor.chain().focus().toggleHeading({ level: 1 }).run(); }} className={`toolbar-button ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`} title="Heading 1"><Heading1 size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleHeading({ level: 2 }).run(); }} className={`toolbar-button ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`} title="Heading 2"><Heading2 size={16} /></button>
      <button
        onClick={() => {
          // First ensure we're in paragraph format
          editor.chain().focus().setParagraph().run();

          // Then toggle text alignment between left and right
          const currentAlignment = editor.getAttributes('paragraph').textAlign || 'left';
          const newAlignment = currentAlignment === 'left' ? 'right' : 'left';
          editor.chain().focus().setTextAlign(newAlignment).run();
        }}
        className={`toolbar-button ${editor.isActive('paragraph') ? 'is-active' : ''}`}
        title="Paragraph (Toggle Left/Right Alignment)"
        style={{
          backgroundColor: editor.isActive('paragraph') && (editor.getAttributes('paragraph').textAlign === 'right') ? 'var(--pmr-bg-subtle)' : undefined
        }}
      >
        &#182;
      </button>

      {/* Custom Font Size Dropdown */}
      <div className="custom-dropdown-container enhanced" ref={fontSizeDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          className="custom-dropdown-button enhanced"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowFontSizeDropdown(!showFontSizeDropdown);
            setShowFontFamilyDropdown(false);
          }}
          title="Font Size"
        >
          <span className="option-text">{FONT_SIZES.find(f => f.value === fontSize)?.label || fontSize}</span>
          <span className={`dropdown-arrow ${showFontSizeDropdown ? 'rotated' : ''}`}>▼</span>
        </button>
        {showFontSizeDropdown && (
          <div className="custom-dropdown-menu enhanced">
            <div className="dropdown-options-container">
              {FONT_SIZES.map((option) => {
                const isSelected = option.value === fontSize;
                console.log('Font size option:', option.value, 'current:', fontSize, 'selected:', isSelected);
                return (
                  <div
                    key={option.value}
                    className={`custom-dropdown-item enhanced ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Font size option clicked:', option.value);
                      handleFontSizeSelect(option.value);
                    }}
                  >
                    <span className="option-text">{option.label}</span>
                    <span className="option-value">{option.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Custom Font Family Dropdown */}
      <div className="custom-dropdown-container enhanced" ref={fontFamilyDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          className="custom-dropdown-button enhanced"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowFontFamilyDropdown(!showFontFamilyDropdown);
            setShowFontSizeDropdown(false);
          }}
          title="Font Family"
        >
          <span className="option-text">{FONT_FAMILIES.find(f => f.value === fontFamily)?.label || fontFamily}</span>
          <span className={`dropdown-arrow ${showFontFamilyDropdown ? 'rotated' : ''}`}>▼</span>
        </button>
        {showFontFamilyDropdown && (
          <div className="custom-dropdown-menu enhanced">
            <div className="dropdown-options-container">
              {FONT_FAMILIES.map((option) => {
                const isSelected = option.value === fontFamily;
                console.log('Font family option:', option.value, 'current:', fontFamily, 'selected:', isSelected);
                return (
                  <div
                    key={option.value}
                    className={`custom-dropdown-item enhanced ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Font family option clicked:', option.value);
                      handleFontFamilySelect(option.value);
                    }}
                  >
                    <span className="option-text">{option.label}</span>
                    {/* <span className="option-value">{option.value}</span> */}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="toolbar-divider"></div>
      {/* Text Color (A icon) */}
      <div style={{ position: 'relative', display: 'inline-block' }} ref={textColorBtnRef}>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowTextColorPalette(v => !v);
            setShowHighlightPalette(false);
          }}
          className={`toolbar-button ${editor.isActive('textStyle', { color: currentTextColor }) ? 'is-active' : ''}`}
          title="Text Color"
        >
          <span style={{ color: currentTextColor, fontWeight: 'bold', fontSize: '18px', fontFamily: 'Arial' }}>A</span>
        </button>
        {showTextColorPalette && (
          <div className="color-palette-popup" style={{ position: 'absolute', top: '110%', left: 0, zIndex: 9999, background: '#fff', border: '2px solid #333', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', padding: 8, minWidth: 180, marginTop: 8 }}>
            <div style={{ color: 'red', fontWeight: 'bold', marginBottom: 4 }}>Text Color Palette</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 20px)', gap: 4 }}>
              {COLOR_PALETTE.map(color => (
                <div
                  key={color}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentTextColor(color);
                    editor.chain().focus().setColor(color).run();
                    setShowTextColorPalette(false);
                  }}
                  style={{ width: 18, height: 18, background: color, border: color === currentTextColor ? '2px solid #333' : '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={() => { setCurrentTextColor('#000000'); editor.chain().focus().unsetColor().run(); }}
        className="toolbar-button"
        title="Clear Text Color"
        style={{ fontSize: '10px', padding: '2px 6px', height: '25px' }}
      >
        Clear
      </button>
      {/* Highlight (marker icon) */}
      <div style={{ position: 'relative', display: 'inline-block' }} ref={highlightBtnRef}>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowHighlightPalette(v => !v);
            setShowTextColorPalette(false);
          }}
          className={`toolbar-button ${editor.isActive('highlight', { color: currentHighlightColor }) ? 'is-active' : ''}`}
          title="Highlight"
        >
          <Highlighter size={16} style={{ color: currentHighlightColor }} />
        </button>
        {showHighlightPalette && (
          <div className="color-palette-popup" style={{ position: 'absolute', top: '110%', left: 0, zIndex: 9999, background: '#fff', border: '2px solid #333', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', padding: 8, minWidth: 180, marginTop: 8 }}>
            <div style={{ color: 'red', fontWeight: 'bold', marginBottom: 4 }}>Highlight Palette</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 20px)', gap: 4 }}>
              {COLOR_PALETTE.map(color => (
                <div
                  key={color}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentHighlightColor(color);
                    editor.chain().focus().setHighlight({ color }).run();
                    setShowHighlightPalette(false);
                  }}
                  style={{ width: 18, height: 18, background: color, border: color === currentHighlightColor ? '2px solid #333' : '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={() => { setCurrentHighlightColor('#ffff00'); editor.chain().focus().unsetHighlight().run(); }}
        className="toolbar-button"
        title="Clear Highlight"
        style={{ fontSize: '10px', padding: '2px 6px', height: '25px' }}
      >
        Clear
      </button>
      <div className="toolbar-divider"></div>
      {/* Bullet List, Ordered List */}
      <button onClick={() => { editor.chain().focus().toggleBulletList().run(); }} className={`toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`} title="Bullet List"><List size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleOrderedList().run(); }} className={`toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`} title="Numbered List"><ListOrdered size={16} /></button>
      <div className="toolbar-divider"></div>
      {/* Subscript, Superscript */}
      <button onClick={() => { editor.chain().focus().toggleSubscript().run(); }} className={`toolbar-button ${editor.isActive('subscript') ? 'is-active' : ''}`} title="Subscript"><LucideSubscript size={16} /></button>
      <button onClick={() => { editor.chain().focus().toggleSuperscript().run(); }} className={`toolbar-button ${editor.isActive('superscript') ? 'is-active' : ''}`} title="Superscript"><LucideSuperscript size={16} /></button>
      <div className="toolbar-divider"></div>
      {/* Text Alignment */}
      <button onClick={() => { editor.chain().focus().setTextAlign('left').run(); }} className={`toolbar-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`} title="Align Left"><AlignLeft size={16} /></button>
      <button onClick={() => { editor.chain().focus().setTextAlign('center').run(); }} className={`toolbar-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`} title="Align Center"><AlignCenter size={16} /></button>
      <button onClick={() => { editor.chain().focus().setTextAlign('right').run(); }} className={`toolbar-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`} title="Align Right"><AlignRight size={16} /></button>
      <div className="toolbar-divider"></div>
      {/* Link, Image, Video */}
      <button
        onClick={() => {
          const previousUrl = editor.getAttributes('link').href || '';
          const url = window.prompt('Enter URL', previousUrl);
          if (url === null) return; // Cancelled
          if (url === '') {
            // Remove link
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
          }
          if (editor.state.selection.empty) {
            // No text selected, insert the URL as text with link
            editor.chain().focus().insertContent(`<a href=\"${url}\">${url}</a>`).run();
          } else {
            // Text selected, apply link
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }
        }}
        className={`toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
        title="Link"
      >
        <LucideLink size={16} />
      </button>
      {/* Image upload button */}
      <button
        onClick={() => {
          console.log('Image button clicked');
          console.log('composeMode:', composeMode, 'editMode:', editMode);
          console.log('isImageUploading:', isImageUploading);
          console.log('imageInputRef.current:', imageInputRef.current);

          // Only allow image upload in compose or edit mode
          if (!composeMode && !editMode) {
            console.log('Not in compose or edit mode');
            alert('Please enter compose or edit mode to add images');
            return;
          }

          // Prevent multiple uploads
          if (isImageUploading) {
            console.log('Already uploading');
            return;
          }

          // Ensure editor is focused before uploading
          if (editor && !editor.isDestroyed) {
            console.log('Focusing editor');
            editor.commands.focus();
          }

          // Create a new file input element and trigger it
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'image/*';
          fileInput.style.display = 'none';

          fileInput.onchange = (event) => {
            console.log('New file input onChange triggered');
            handleImageUpload(event);
            // Clean up the temporary input
            document.body.removeChild(fileInput);
          };

          // Add to DOM and trigger
          document.body.appendChild(fileInput);
          console.log('Triggering new file input click');
          fileInput.click();
        }}
        className={`toolbar-button ${(!composeMode && !editMode) ? 'disabled' : ''} ${isImageUploading ? 'uploading' : ''}`}
        title={isImageUploading ? "Uploading..." : "Upload Image"}
        disabled={!composeMode && !editMode || isImageUploading}
      >
        {isImageUploading ? (
          <span style={{ fontSize: '12px' }}>⏳</span>
        ) : (
          <LucideImage size={16} />
        )}
      </button>
      <input
        type="file"
        accept="image/*"
        ref={imageInputRef}
        style={{ display: 'none' }}
        onChange={handleImageUpload}
        onClick={(e) => {
          console.log('File input clicked directly');
        }}
        onFocus={(e) => {
          console.log('File input focused');
        }}
        onBlur={(e) => {
          console.log('File input blurred');
        }}
      />
      {/* Video (YouTube embed) */}
      <button
        onClick={() => {
          // Only allow video insertion in compose or edit mode
          if (!composeMode && !editMode) {
            alert('Please enter compose or edit mode to add videos');
            return;
          }
          setShowVideoModal(true);
        }}
        className={`toolbar-button ${(!composeMode && !editMode) ? 'disabled' : ''}`}
        title="Video"
        disabled={!composeMode && !editMode}
      >
        <LucideVideo size={16} />
      </button>
    </div>
  );
};

const NoteApp = (props) => {
  const [noteDataLst, setNoteDataLst] = useState([]);
  const [selectedNote, setSelectedNote] = useState(false);
  const [composeMode, setComposeMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [isDictating, setIsDictating] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [dictationStatus, setDictationStatus] = useState('');
  const [dictationSessionId, setDictationSessionId] = useState(0);
  const recognitionRef = useRef(null);
  const dispatch = useDispatch();
  const token = useSelector(state => state.apiService.token);
  const userData = useSelector(state => state.apiService.login);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIcon, setDeleteIcon] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [archiveGeader, setArchiveGeader] = useState('');
  const [showcloseButton, setShowcloseButton] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState('');

  // Video Modal State
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Image upload state
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadMessage, setImageUploadMessage] = useState('');

  const selectedUserId = props.selectedFamilyMember?.user_id || userData?.user_id;
  const params = {
    UserID: selectedUserId,
    token: token
  }
  const notesParams = {
    userID: selectedUserId,
    token: token,
    notes: newNoteText,
    title: newNoteTitle,
    notesID: null
  };
  const [updatedNoteParams, setUpdatedNoteParams] = useState({
    userID: selectedUserId,
    token: token,
    notes: newNoteText,
    title: newNoteTitle,
    notesID: null
  });
  const [deleteNoteParams, setDeleteNoteParams] = useState({
    userID: selectedUserId,
    token: token,
    notesID: ""
  });
  const handleNoteChange = (content) => {
    console.log("content value", content);
    setNewNoteText(content);
  }

  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentHighlightColor, setCurrentHighlightColor] = useState('#ffff00');
  const [showTextColorPalette, setShowTextColorPalette] = useState(false);
  const [showHighlightPalette, setShowHighlightPalette] = useState(false);
  const textColorBtnRef = useRef();
  const highlightBtnRef = useRef();
  const imageInputRef = useRef();

  // Click outside to close palettes
  useEffect(() => {
    function handleClickOutside(event) {
      // Don't handle clicks on select dropdowns or their options
      if (event.target.tagName === 'SELECT' ||
        event.target.tagName === 'OPTION' ||
        event.target.closest('select')) {
        return;
      }

      // Close text color palette if clicking outside both button and palette
      if (textColorBtnRef.current && !textColorBtnRef.current.contains(event.target)) {
        setShowTextColorPalette(false);
      }

      // Close highlight palette if clicking outside both button and palette
      if (highlightBtnRef.current && !highlightBtnRef.current.contains(event.target)) {
        setShowHighlightPalette(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to close all palettes
  const closeAllPalettes = () => {
    setShowTextColorPalette(false);
    setShowHighlightPalette(false);
  };

  // Custom Iframe Extension for Video Embeds
  const Iframe = Node.create({
    name: 'iframe',
    group: 'block',
    atom: true,

    addAttributes() {
      return {
        src: {
          default: null,
        },
        title: {
          default: null,
        },
        frameborder: {
          default: '0',
        },
        allowfullscreen: {
          default: true,
        },
        allow: {
          default: 'autoplay; encrypted-media; picture-in-picture',
        },
        style: {
          default: 'width: 100%; height: 315px; max-width: 560px; margin: 10px 0; border-radius: 4px;',
        },
      }
    },

    parseHTML() {
      return [
        {
          tag: 'iframe',
        },
      ]
    },

    renderHTML({ HTMLAttributes }) {
      return ['iframe', HTMLAttributes]
    },

    addCommands() {
      return {
        setIframe: (options) => ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
      }
    },
  });

  // Ensure editor focus before commands
  const ensureEditorFocus = () => {
    if (editor) {
      // Always focus the editor to ensure commands work properly
      setTimeout(() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.focus();
        }
      }, 0);
    }
  };

  // TipTap Editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Strike,
      Code, // Move Code before CodeBlock
      CodeBlock,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      Highlight,
      Link,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-image',
          style: 'max-width: 100%; height: auto; display: block; margin: 10px 0; border-radius: 4px;',
        },
        parseHTML() {
          return [
            {
              tag: 'img[src]',
            },
          ]
        },
        renderHTML({ HTMLAttributes }) {
          return ['img', HTMLAttributes]
        },
      }),
      Iframe,
      Placeholder.configure({ placeholder: 'Write your note...' }),
      FontFamily,
      FontSize,
    ],
    content: newNoteText,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Only update state if we're in compose or edit mode to avoid conflicts
      if (composeMode || editMode) {
        setNewNoteText(html);
        if (validationMessage) setValidationMessage(''); // Clear validation message when typing
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  // Handler for file input change
  const handleImageUpload = (event) => {
    console.log('handleImageUpload called');
    // alert('File input triggered!'); // Temporary alert to test if function is called
    const file = event.target.files && event.target.files[0];
    if (!file) {
      console.log('No file selected');
      alert('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      console.log('Invalid file type:', file.type);
      setImageUploadMessage('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      setTimeout(() => setImageUploadMessage(''), 2000);
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      console.log('File too large:', file.size);
      setImageUploadMessage('Image size should be less than 5MB');
      setTimeout(() => setImageUploadMessage(''), 2000);
      return;
    }

    // Set uploading state
    setIsImageUploading(true);
    setImageUploadMessage('Uploading image...');
    console.log('Starting image upload...');

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      console.log('File read successfully, data URL length:', dataUrl.length);

      if (editor && !editor.isDestroyed) {
        console.log('Editor is ready, attempting to insert image');
        try {
          // Ensure editor is focused
          editor.commands.focus();

          // Create image HTML with proper styling
          const imgHtml = `<img src="${dataUrl}" alt="${file.name}" title="${file.name}" style="max-width: 100%; height: auto; display: block; margin: 10px 0; border-radius: 4px;" />`;
          console.log('Image HTML created:', imgHtml.substring(0, 100) + '...');

          // Try multiple insertion methods
          let success = false;

          // Method 1: Direct HTML insertion
          try {
            console.log('Trying direct HTML insertion...');
            success = editor.chain().focus().insertContent(imgHtml).run();
            if (success) {
              console.log('Direct HTML insertion successful');
            }
          } catch (error) {
            console.log('Direct HTML insertion failed:', error);
          }

          // Method 2: Insert as paragraph with image
          if (!success) {
            try {
              console.log('Trying paragraph insertion...');
              const paragraphHtml = `<p>${imgHtml}</p>`;
              success = editor.chain().focus().insertContent(paragraphHtml).run();
              if (success) {
                console.log('Paragraph insertion successful');
              }
            } catch (error) {
              console.log('Paragraph insertion failed:', error);
            }
          }

          // Method 3: Insert at specific position
          if (!success) {
            try {
              console.log('Trying position-based insertion...');
              const currentPos = editor.state.selection.from;
              success = editor.chain().focus().insertContentAt(currentPos, imgHtml).run();
              if (success) {
                console.log('Position-based insertion successful');
              }
            } catch (error) {
              console.log('Position-based insertion failed:', error);
            }
          }

          // Method 4: Replace current selection
          if (!success) {
            try {
              console.log('Trying selection replacement...');
              success = editor.chain().focus().replaceContent(imgHtml).run();
              if (success) {
                console.log('Selection replacement successful');
              }
            } catch (error) {
              console.log('Selection replacement failed:', error);
            }
          }

          if (success) {
            console.log('Image inserted successfully using one of the methods');
            // Update the note content to trigger re-render
            const updatedContent = editor.getHTML();
            setNewNoteText(updatedContent);
            setImageUploadMessage('Image uploaded successfully!');
            setIsImageUploading(false);
            setTimeout(() => setImageUploadMessage(''), 2000);
          } else {
            console.error('All image insertion methods failed');
            setImageUploadMessage('Failed to insert image. Please try again.');
            setIsImageUploading(false);
            setTimeout(() => setImageUploadMessage(''), 2000);
          }

        } catch (error) {
          console.error('Error inserting image:', error);
          setImageUploadMessage('Failed to insert image. Please try again.');
          setIsImageUploading(false);
          setTimeout(() => setImageUploadMessage(''), 2000);
        }
      } else {
        console.log('Editor not ready');
        setImageUploadMessage('Editor not ready. Please try again.');
        setIsImageUploading(false);
        setTimeout(() => setImageUploadMessage(''), 2000);
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading image file:', error);
      setImageUploadMessage('Error reading image file');
      setIsImageUploading(false);
      setTimeout(() => setImageUploadMessage(''), 2000);
    };

    reader.readAsDataURL(file);

    // Reset input value so the same file can be selected again
    event.target.value = '';
  };

  // Update editor content when a note is selected (view mode)
  useEffect(() => {
    console.log('Editor useEffect triggered:', {
      hasEditor: !!editor,
      selectedNote: selectedNote?.Title,
      composeMode,
      editMode
    });
    if (editor && selectedNote && !composeMode && !editMode) {
      console.log('Setting editor content for:', selectedNote.Title);
      const content = selectedNote.Notes || '';
      if (editor.getHTML() !== content) {
        editor.chain().setContent(content).run();
      }
    }
  }, [editor, selectedNote, composeMode, editMode]);

  // Update editor content when newNoteText changes (only for compose/edit modes)
  useEffect(() => {
    if (editor && (composeMode || editMode)) {
      const currentContent = editor.getHTML();
      // Only update if content is different and we're not in the middle of an update
      if (currentContent !== newNoteText && newNoteText !== '') {
        // Use setTimeout to avoid infinite loops
        setTimeout(() => {
          if (editor && !editor.isDestroyed && (composeMode || editMode)) {
            editor.chain().setContent(newNoteText).run();
          }
        }, 0);
      }
    }
  }, [editor, newNoteText, composeMode, editMode]);

  // Check browser compatibility for speech recognition
  const checkSpeechRecognitionSupport = () => {
    const isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    console.log('Speech recognition support:', isSupported);
    return isSupported;
  };

  // Initialize speech recognition
  useEffect(() => {
    initializeSpeechRecognition();
  }, []);

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setIsDictating(true);
        setDictationStatus('Listening... Speak now');

        // Increment session ID to track new dictation sessions
        setDictationSessionId(prev => prev + 1);
      };

      recognitionRef.current.onresult = (event) => {
        // Get only the current session's transcript
        let currentTranscript = '';

        // Get the latest result only
        if (event.results.length > 0) {
          const latestResult = event.results[event.results.length - 1];
          currentTranscript = latestResult[0].transcript;
        }

        // Only update if it's a final result
        if (event.results[event.results.length - 1].isFinal) {
          if (editor) {
            // Check if editor already has content and add space if needed
            const currentContent = editor.getHTML();
            const hasExistingContent = currentContent.trim() !== '';

            // Add space before new content if there's existing content
            const contentToInsert = hasExistingContent ? ' ' + currentTranscript : currentTranscript;

            // Insert only the current transcript, not accumulated text
            editor.chain().focus().insertContent(contentToInsert).run();
          } else {
            // Add space before new content if there's existing text
            setNewNoteText(prev => {
              const hasExistingText = prev.trim() !== '';
              return hasExistingText ? prev + ' ' + currentTranscript : prev + currentTranscript;
            });
          }
          setDictationStatus('Text added successfully');
          // Clear status after 2 seconds
          setTimeout(() => setDictationStatus(''), 2000);
        } else {
          setDictationStatus('Listening... Speak now');
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsDictating(false);
        setDictationStatus('');

        // Handle specific errors
        switch (event.error) {
          case 'not-allowed':
            setDictationStatus('Microphone access denied');
            alert('Microphone access denied. Please allow microphone access in your browser settings.');
            break;
          case 'no-speech':
            setDictationStatus('No speech detected');
            console.log('No speech detected');
            break;
          case 'audio-capture':
            setDictationStatus('No microphone found');
            alert('No microphone found. Please connect a microphone and try again.');
            break;
          case 'network':
            setDictationStatus('Network error');
            alert('Network error occurred. Please check your internet connection.');
            break;
          default:
            setDictationStatus('Error occurred');
            console.error('Speech recognition error:', event.error);
        }

        // Clear error status after 3 seconds
        setTimeout(() => setDictationStatus(''), 3000);
      };

      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        setIsDictating(false);
        setDictationStatus('');
      };
    } else {
      console.warn('Speech recognition not supported in this browser');
    }

    // Cleanup
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  };

  // Add keyboard shortcut for dictation
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+D for dictation toggle
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        if (composeMode || editMode) {
          handleToggleDictation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [composeMode, editMode]);

  // --- Mock Data ---
  useEffect(() => {
    fetchNotes();
    setNoteDataLst([]);
  }, [dispatch, params.UserID, params.token, props.selectedFamilyMember]);
  // --- End Mock Data ---

  const fetchNotes = () => {
    setNoteDataLst([]);

    dispatch(getNotes(params)).then((res) => {
      console.log(res, "res for getNotes");
      if (res.payload !== "") {
        const fetchedNotes = res.payload.Table.map(note => ({
          Title: note.Title || 'Untitled Note',
          Notes: note.Notes || '',
          Created_on: note.Created_on ? new Date(note.Created_on) : new Date(),
          Updated_on: note.Updated_on ? new Date(note.Updated_on) : null,
          notesID: note.Note_id
        }));
        setNoteDataLst(fetchedNotes);
      } else {
        setNoteDataLst([]);
        console.warn("getNotes API did not return the expected data structure.");
      }
    }).catch(error => {
      console.error("Error fetching notes:", error);
    });
  }

  const updateNotes = (noteToUpdate) => {
    console.log("updateNotes called with:", noteToUpdate);
    dispatch(updateNote(noteToUpdate)).then((res) => {
      console.log("res for updateNote", res);
      if (res.payload) {
        fetchNotes();
        console.log("Setting success message for updated note");
        setSuccessModalMessage('Note updated successfully');
        setShowSuccessModal(true);
        setSelectedNote(null);
      }
    }).catch(error => {
      console.error("Error updating note:", error);
    });
  }

  const handleComposeNewNote = () => {
    setSelectedNote(null);
    setValidationMessage('');
    setEditMode(false);
    setNewNoteTitle('');
    setNewNoteText('');
    setComposeMode(true);

    // Clear the editor content
    if (editor && !editor.isDestroyed) {
      editor.chain().focus().clearContent().run();
    }
  };

  const handleSelectNote = (note) => {
    console.log('handleSelectNote called with:', note.Title);
    setComposeMode(false);
    setEditMode(false);
    setSelectedNote(note);
    setNewNoteTitle(note.Title || '');
    setNewNoteText(note.Notes || '');
  };

  const handleEditSelectedNote = () => {
    if (selectedNote) {
      setComposeMode(false);
      setEditMode(true);
      setNewNoteTitle(selectedNote.Title || '');
      setNewNoteText(selectedNote.Notes || '');
    }
  };

  const handleDeleteNote = () => {
    if (selectedNote) {
      setShowDeleteConfirm(true);
      setDeleteIcon(true);
      setDeleteTitle('Are you sure ?');
      setDeleteMessage('Do you really want to delete  <b>' + selectedNote.Title + '</b>  note?');
      setShowcloseButton(true);
      setDeleteNoteParams({
        userID: selectedUserId,
        token: token,
        notesID: selectedNote.notesID
      });
    }
  };

  const OnConfirmDeleteNote = () => {
    dispatch(deleteNote(deleteNoteParams)).then((res) => {
      console.log("res for deleteNote", res);
      if (res.payload) {
        setShowDeleteConfirm(false);
        setSuccessModalMessage('Note deleted successfully');
        setShowSuccessModal(true);
        setSelectedNote(null);
        fetchNotes();
      }
    }).catch(error => {
      console.error("Error deleting note:", error);
    });
  }
  const handleSaveNote = () => {
    // Validation: Check if both title and notes are empty
    const trimmedTitle = newNoteTitle.trim();
    const trimmedNotes = newNoteText.replace(/<[^>]*>/g, '').trim(); // Remove HTML tags for validation
    const hasVisual = hasVisualContent(newNoteText); // Check for images/videos

    if (!trimmedTitle && !trimmedNotes && !hasVisual) {
      setValidationMessage('Please add title and notes before saving.');
      return;
    } else if (!trimmedTitle) {
      setValidationMessage('Please add title before saving.');
      return;
    } else if (!trimmedNotes && !hasVisual) {
      setValidationMessage('Please add notes before saving.');
      return;
    } else {
      setValidationMessage('');
      setIsAutosaving(true);
      console.log("Autosaving started...");

      // Create the note data with current content
      const noteData = {
        userID: selectedUserId,
        token: token,
        notes: newNoteText,
        title: newNoteTitle,
        notesID: null
      };

      // Add to local list for immediate feedback
      const tempNote = {
        Title: newNoteTitle,
        Notes: newNoteText,
        Created_on: new Date(),
        Updated_on: null,
        notesID: `temp-${Date.now()}`
      };

      setNoteDataLst([tempNote, ...noteDataLst]);
      setSelectedNote(tempNote);
      setComposeMode(false);
      setEditMode(false);
      setNewNoteText('');
      setNewNoteTitle('');
      setIsAutosaving(false);
      console.log("Autosaving finished.");

      dispatch(createNote(noteData)).then((res) => {
        console.log("createNote response:", res);
        if (res.payload) {
          fetchNotes();
          console.log("Setting success message for new note");
          setSuccessModalMessage('Note saved successfully');
          setShowSuccessModal(true);
          setSelectedNote(null);
        }
      }).catch(error => {
        console.error("Error creating note:", error);
        // Show error message if save fails
        setSuccessModalMessage('Failed to save note. Please try again.');
        setShowSuccessModal(true);
      });
    }
  };

  const handleUpdateNote = () => {
    if (selectedNote) {
      // Validation: Check if both title and notes are empty
      const trimmedTitle = newNoteTitle.trim();
      const trimmedNotes = newNoteText.replace(/<[^>]*>/g, '').trim(); // Remove HTML tags for validation
      const hasVisual = hasVisualContent(newNoteText); // Check for images/videos

      if (!trimmedTitle && !trimmedNotes && !hasVisual) {
        setValidationMessage('Please add title and notes before updating.');
        return;
      } else if (!trimmedTitle) {
        setValidationMessage('Please add title before updating.');
        return;
      } else if (!trimmedNotes && !hasVisual) {
        setValidationMessage('Please add notes before updating.');
        return;
      } else {
        setValidationMessage('');
        setIsAutosaving(true);
        const updatedNote = {
          userID: selectedUserId,
          token: token,
          title: newNoteTitle,
          notes: "<p>" + newNoteText + "</p>",
          notesID: selectedNote.notesID
        };
        setEditMode(false);
        setComposeMode(false);
        setIsAutosaving(false);
        updateNotes(updatedNote);
      }
    }
  };

  const handleCancelNote = () => {
    setComposeMode(false);
    setEditMode(false);
    setNewNoteTitle(selectedNote ? selectedNote.Title || '' : '');
    setNewNoteText(selectedNote ? selectedNote.Notes || '' : '');
    if (editor && selectedNote) {
      editor.chain().focus().setContent(selectedNote.Notes || '').run();
    }
  };

  const handleResetNote = () => {
    setNewNoteTitle('');
    setNewNoteText('');
    if (editor) {
      editor.chain().focus().setContent('').run();
    }
  };

  const handleToggleDictation = async () => {
    console.log('handleToggleDictation called, isDictating:', isDictating);

    if (!checkSpeechRecognitionSupport()) {
      console.error('Speech recognition not available');
      alert('Speech recognition is not supported in your browser. Please use a modern browser like Chrome, Firefox, or Edge.');
      return;
    }

    if (!isDictating) {
      try {
        console.log('Checking microphone permissions...');
        // Check microphone permission first
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({ name: 'microphone' });
          console.log('Microphone permission state:', permission.state);
          if (permission.state === 'denied') {
            alert('Microphone access is denied. Please allow microphone access in your browser settings and refresh the page.');
            return;
          }
        }

        // Ensure we're in compose or edit mode
        if (!composeMode && !editMode) {
          alert('Please start composing or editing a note before using dictation.');
          return;
        }

        // Ensure editor is focused
        if (editor) {
          editor.chain().focus().run();
        }

        // Reinitialize speech recognition for clean session
        initializeSpeechRecognition();

        // Small delay to ensure clean restart
        setTimeout(() => {
          console.log('Starting speech recognition...');
          recognitionRef.current.start();
          console.log("Starting dictation...");
        }, 100);

      } catch (error) {
        console.error("Error starting dictation:", error);
        alert('Failed to start dictation. Please check your microphone permissions and try again.');
        setIsDictating(false);
      }
    } else {
      try {
        console.log('Stopping speech recognition...');
        recognitionRef.current.stop();
        console.log("Stopping dictation...");
      } catch (error) {
        console.error("Error stopping dictation:", error);
        setIsDictating(false);
      }
    }
  };

  const getNotePreview = (note) => {
    if (!note || !note.Notes) return '';
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = note.Notes;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '');
    } catch (error) {
      console.error("Error generating note preview:", error);
      return 'Preview unavailable';
    }
  };

  // Helper function to check if content contains images or videos
  const hasVisualContent = (htmlContent) => {
    if (!htmlContent) return false;

    // Check for images
    const hasImages = /<img[^>]*>/i.test(htmlContent);

    // Check for iframes (videos)
    const hasVideos = /<iframe[^>]*>/i.test(htmlContent);

    return hasImages || hasVideos;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    return dateObj.toLocaleString('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };



  return (
    <div className="note-app-container">
      <main className="note-app-main">
        <aside className="note-list-column">
          <header className="note-app-header sticky-header">
            <div className="header-content">
              <h1 className="note-app-title"><b>Saved Notes</b></h1>
              <div className="note-app-actions">
                <button
                  onClick={handleComposeNewNote}
                  className={`note-add-button ${editMode ? 'disabled' : ''}`}
                  title={editMode ? "Please save or cancel current edit first" : "Compose New Note"}
                  disabled={editMode}
                  style={editMode ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  +
                </button>
              </div>
            </div>
          </header>
          <div className="note-list scrollable-list">
            {noteDataLst.length === 0 ? (
              <div className="no-note-selected">
                No notes found.
              </div>
            ) : (
              noteDataLst.map((note, i) => {
                const noteKey = note.notesID || `temp-${i}`;
                const isSelected = selectedNote && (
                  (selectedNote.notesID && selectedNote.notesID === note.notesID) ||
                  (!selectedNote.notesID && !note.notesID && selectedNote === note)
                );
                return (
                  <div
                    key={noteKey}
                    className={classNames(
                      "note-card",
                      {
                        "white-bg": i % 2 === 0,
                        "cement-bg": i % 2 !== 0,
                        "selected": isSelected,
                      }
                    )}
                    onClick={() => handleSelectNote(note)}
                  >
                    <div className="note-info">
                      <div className="note-title-wrapper">
                        <div className="note-title-preview">
                          <b>{note.Title || 'Untitled Note'}</b>
                        </div>
                      </div>
                      <div className="note-content-preview">
                        {getNotePreview(note)}
                      </div>
                      <div className="note-date">
                        {formatDate(note.Updated_on || note.Created_on)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <section className="note-detail-column">
          {selectedNote && !composeMode && !editMode && (
            <div className="note-detail">
              <div className="note-header">
                <div className="note-title-wrapper">
                  <h2 className="note-title">
                    <b>{selectedNote.Title || 'Untitled Note'}</b>
                  </h2>
                </div>
                <div className="note-actions">
                  <button
                    onClick={handleEditSelectedNote}
                    className="btn btn-primary"
                    title="Edit Note"
                  >
                    <i className="fa fa-edit"></i>
                  </button>
                  <button
                    onClick={handleDeleteNote}
                    className="btn btn-danger ml-1"
                    title="Delete Note"
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              </div>
              <div className="note-content">
                <div className="note-content-scroll" dangerouslySetInnerHTML={{ __html: selectedNote.Notes || '' }} />
              </div>
              <footer className="note-footer">
                <div className="d-flex justify-content-end">
                  <small className="note-detail-date">
                    {selectedNote.Updated_on
                      ? `Updated on: ${formatDate(selectedNote.Updated_on)}`
                      : `Created on: ${formatDate(selectedNote.Created_on)}`}
                  </small>
                </div>
              </footer>
            </div>
          )}

          {(composeMode || editMode) && (
            <div className="note-detail compose-mode">
              {/* Title Input Field - Fixed at top */}
              <div className="note-inputs mb-3" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white', padding: '10px 0', borderBottom: '1px solid #e9ecef' }}>
                <input
                  type="text"
                  id="note-title"
                  className="form-control note-title-input"
                  placeholder="Note Title"
                  value={newNoteTitle}
                  onChange={(e) => {
                    setNewNoteTitle(e.target.value);
                    if (validationMessage) setValidationMessage(''); // Clear validation message when typing
                  }}
                  style={{
                    fontSize: '1rem',
                    padding: '0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '5px',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                />
              </div>

              {/* Editor Section */}
              <div className="quill-editor-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="tiptap-editor" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Toolbar
                    editor={editor}
                    composeMode={composeMode}
                    editMode={editMode}
                    isImageUploading={isImageUploading}
                    imageInputRef={imageInputRef}
                    handleImageUpload={handleImageUpload}
                    showVideoModal={showVideoModal}
                    setShowVideoModal={setShowVideoModal}
                    currentTextColor={currentTextColor}
                    setCurrentTextColor={setCurrentTextColor}
                    currentHighlightColor={currentHighlightColor}
                    setCurrentHighlightColor={setCurrentHighlightColor}
                    showTextColorPalette={showTextColorPalette}
                    setShowTextColorPalette={setShowTextColorPalette}
                    showHighlightPalette={showHighlightPalette}
                    setShowHighlightPalette={setShowHighlightPalette}
                    textColorBtnRef={textColorBtnRef}
                    highlightBtnRef={highlightBtnRef}
                  />
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <EditorContent editor={editor} />
                  </div>
                </div>
              </div>

              {/* Footer with Buttons - Fixed at bottom */}
              <footer className="note-footer" style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                backgroundColor: 'white',
                padding: '15px 0',
                borderTop: '1px solid #e9ecef',
                marginTop: 'auto'
              }}>
                <div className="footer-row">
                  {/* Left: Dictation Controls */}
                  <div className="footer-left">
                    <div className="dictation-container d-flex align-items-center gap-2">
                      <button
                        onClick={handleToggleDictation}
                        className="dictation-button"
                        title={!checkSpeechRecognitionSupport() ? 'Speech recognition not supported in this browser' : (isDictating ? 'Click to stop dictation' : 'Click to start voice dictation (Ctrl+Shift+D)')}
                        disabled={!checkSpeechRecognitionSupport()}
                        style={{
                          color: 'white',
                          border: 'none',
                          backgroundColor: !checkSpeechRecognitionSupport() ? 'var(--pmr-text-muted)' : 'var(--pmr-primary)',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: !checkSpeechRecognitionSupport() ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          whiteSpace: 'nowrap',
                          transition: 'background-color 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          opacity: !checkSpeechRecognitionSupport() ? 0.6 : 1
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                        {!checkSpeechRecognitionSupport() ? 'Dictation Not Supported' : (isDictating ? 'Stop Dictation' : 'Start Dictation (Ctrl+Shift+D)')}
                        {isDictating && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              animation: 'pulse 1.5s infinite',
                              marginLeft: '4px'
                            }}
                          />
                        )}
                      </button>
                      <button
                        onClick={handleResetNote}
                        className="btn btn-sm btn-danger"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Clear
                      </button>
                      {isDictating && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666', fontSize: '14px' }}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              position: 'relative',
                              animation: 'spin 1s linear infinite'
                            }}>
                              {[...Array(8)].map((_, i) => (
                                <div
                                  key={i}
                                  style={{
                                    position: 'absolute',
                                    width: '3px',
                                    height: '3px',
                                    backgroundColor: '#666',
                                    borderRadius: '50%',
                                    top: '50%',
                                    left: '50%',
                                    transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-6px)`,
                                    opacity: i === 0 ? 1 : 0.3
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                          <span>{dictationStatus || 'Listening...'}</span>
                        </div>
                      )}
                      {dictationStatus && !isDictating && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: dictationStatus.includes('Error') || dictationStatus.includes('denied') || dictationStatus.includes('found') ? '#dc3545' : '#28a745',
                          fontSize: '14px',
                          fontStyle: 'italic'
                        }}>
                          <span>{dictationStatus}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="autosave-indicator text-center mt-2">
                    {isAutosaving && (
                      <span className="autosave-message" style={{ color: '#007bff', fontWeight: '500' }}>
                        Autosaving...
                      </span>
                    )}
                    {validationMessage && (
                      <span className="validation-message" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                        {validationMessage}
                      </span>
                    )}
                    {imageUploadMessage && (
                      <span
                        className={isImageUploading ? "image-upload-loading" : (imageUploadMessage.includes('successfully') ? "image-upload-success" : "image-upload-error")}
                        style={{
                          color: isImageUploading ? '#007bff' : (imageUploadMessage.includes('successfully') ? '#28a745' : '#dc3545'),
                          fontWeight: '500',
                          display: 'block',
                          marginTop: '5px'
                        }}
                      >
                        {isImageUploading && <span style={{ marginRight: '5px' }}>⏳</span>}
                        {imageUploadMessage}
                      </span>
                    )}
                  </div>
                  {/* Right: Action Buttons */}
                  <div className="footer-right">
                    <div className="note-actions d-flex align-items-center gap-2">
                      {editMode ? (
                        <button
                          onClick={handleUpdateNote}
                          className="btn btn-sm btn-primary save-button"
                          disabled={isAutosaving}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Update
                        </button>
                      ) : (
                        <button
                          onClick={handleSaveNote}
                          className="btn btn-sm btn-primary save-button"
                          disabled={isAutosaving}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Save
                        </button>
                      )}
                      <button
                        onClick={handleCancelNote}
                        className="btn btn-sm btn-danger cancel-button"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
                {/* Autosave Indicator (optional, can be placed below or above if needed) */}

              </footer>
            </div>
          )}

          {!selectedNote && !composeMode && !editMode && (
            <div className="no-note-selected">
              <p>Select a note to view its details, or create a new one.</p>
            </div>
          )}
        </section>
      </main>
      {showDeleteConfirm && (
        <DeleteNotes
          show={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          handleClose={() => setShowDeleteConfirm(false)}
          icon={deleteIcon}
          title={deleteTitle}
          message={deleteMessage}
          header={archiveGeader}
          closeButton={showcloseButton}
          onConfirm={OnConfirmDeleteNote}
        />
      )}

      {showVideoModal && (
        <VideoModal
          show={showVideoModal}
          onHide={() => setShowVideoModal(false)}
          onInsert={(iframeData) => {
            if (editor && !editor.isDestroyed) {
              editor.chain().focus().setIframe(iframeData).run();
              // Update the note content to trigger re-render
              const updatedContent = editor.getHTML();
              setNewNoteText(updatedContent);
            }
          }}
        />
      )}
      {showSuccessModal && (
        <CustomSuccessModal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          message={successModalMessage}
        />
      )}
    </div>
  );
};

export default NoteApp;

